import React, { Fragment, } from 'react';
import { uniqBy, keyBy, sortBy, omit, sumBy, } from 'lodash';
import { toast } from 'react-toastify';

import firebase from '../firebase';
import { batch } from '../shared/firebase';
import { fields as settingFields, } from '../shared/models/changesTableImportSetting';
import { withConsolidationAccountItem, } from '../shared/models/trial';
import { uniqAccountName, } from '../shared/util';
import ModalButton from './ModalButton';
import EditButton from './EditButton';
import ProgressButton from './ProgressButton';
import ModelFormModal from './modals/ModelFormModal';

const { keys, entries } = Object;
const db = firebase.firestore();

export default function ChangesTableImportModalButton (props) {
  const {
    company, collectionName, itemsRef, fields, fieldsArg, filterTrials, itemTrials, trialBalances,
    accountItems, accountItemsByName, consolidationAccountItemsById, prevEndTrialBalancesByUniqName,
    relatedCompany, subsidiaryId, period, yearMonth, existingItems, savedSetting, disabled,
  } = props;
  const settingRef = company.ref.collection('changesTableImportSettings').doc(`${collectionName}_${subsidiaryId}`);
  const accountItemsById = keyBy(accountItems, 'id');

  // 現状の取込ロジックと同じソースで取込対象候補を作る（科目名でユニーク化）
  const candidateBalances = uniqBy(
    [...itemTrials.map(_ => ({ ..._, type: 'itemTrial' })), ...trialBalances.map(_ => ({ ..._, type: 'trial' }))],
    'account_item_name',
  ).map(_ => withConsolidationAccountItem(_, accountItemsByName, consolidationAccountItemsById));
  const balancesByAccountItemId = keyBy(
    candidateBalances.filter(_ => accountItemsByName[_.account_item_name] != null),
    _ => accountItemsByName[_.account_item_name].id,
  );

  // 設定があればそれ、無ければ filterTrials 通過科目を対象にする
  const targetAccountItemIds = savedSetting?.accountItemIds
    ?? candidateBalances.filter(filterTrials).map(_ => accountItemsByName[_.account_item_name]?.id).filter(_ => _);
  const selectedAccountItems = sortBy(targetAccountItemIds.map(_ => accountItemsById[_]).filter(_ => _), 'name');

  const onClickImport = async () => {
    if(!window.confirm('試算表から取り込みます。よろしいですか？')) return;

    const targets = targetAccountItemIds.map(_ => balancesByAccountItemId[_]).filter(_ => _);
    await batch(db, targets, (batch, balance) => {
      const accountItemId = accountItemsByName[balance.account_item_name]?.id;
      if(accountItemId == null || existingItems.some(_ => _.accountItemId === accountItemId)) return;

      const prevEndTrialBalance = prevEndTrialBalancesByUniqName[uniqAccountName(relatedCompany, balance.account_item_name, balance.key)];
      const data = {
        accountItemId,
        opening: prevEndTrialBalance?.closing_balance ?? 0,
        ...entries(omit(fields(fieldsArg), ['accountItemId', 'opening'])).reduce((x, [field, { label, }]) => {
          const filterItems = field === 'other' ? (_ => !Object.values(omit(fields(fieldsArg), 'other')).map(_ => _.label).includes(_.name)) : (_ => _.name === label);
          const amount = (balance.type === 'trial' && field === 'other') ? (balance.closing_balance - (prevEndTrialBalance?.closing_balance ?? 0)) : sumBy(balance.items?.filter(filterItems), _ => _.closing_balance - _.opening_balance);
          return {
            ...x,
            [field]: amount,
          };
        }, {}),
        subsidiaryId,
        period,
        yearMonth,
        createdAt: new Date(),
      };
      if(keys(omit(fields(fieldsArg), 'accountItemId')).every(_ => data[_] === 0)) return;

      batch.set(itemsRef.doc(), data);
    });
    toast.success('試算表から取り込みました。');
  };

  return (
    <ModalButton
      color="primary"
      label="試算表から取込み"
      title="試算表から取込み"
      disabled={disabled}
      content={_ => (
        <Fragment>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">取込む科目</span>
            <EditButton
              label="設定"
              icon={<span className="fas fa-cog" />}
              color="secondary"
              outline
              size="sm"
              type="set"
              itemRef={settingRef}
              item={savedSetting ?? { accountItemIds: targetAccountItemIds }}
              FormModal={ModelFormModal}
              processValues={_ => ({ ..._, collectionName, subsidiaryId, updatedAt: new Date(), })}
              formProps={{ title: '取込設定', fields: settingFields({ accountItems }), }}
            />
          </div>
          <div className="overflow-auto mb-3" style={{ maxHeight: 'min(400px, 50vh)', }}>
            {
              selectedAccountItems.length > 0 ? (
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    {
                      selectedAccountItems.map(_ => (
                        <tr key={_.id}>
                          <td>
                            {_.name}
                            {existingItems.some(e => e.accountItemId === _.id) && <span className="badge badge-light ml-2">取込済み</span>}
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              ) : (
                <div className="text-muted text-center py-3">取込む科目がありません。「設定」から科目を選択してください。</div>
              )
            }
          </div>
          <div className="d-flex justify-content-end">
            <ProgressButton color="primary" process={onClickImport} disabled={selectedAccountItems.length === 0}>
              取込
            </ProgressButton>
          </div>
        </Fragment>
      )}
    />
  );
};
